import { useState } from "react";
import { toast } from "sonner";
import { Search, Check, X, Ban, RotateCcw, Trash2, Eye } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { useAdmin, RoleChip, ROLE_LABEL } from "@/admin/AdminContext";
import { api, errMsg, timeAgo, money } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_C = { pending: "text-[#D97706] bg-[#D97706]/10", approved: "text-[#059669] bg-[#059669]/10", suspended: "text-[#DC2626] bg-[#DC2626]/10", rejected: "text-zinc-500 bg-zinc-100" };
const TABS = [["", "All"], ["pending", "Pending"], ["approved", "Active"], ["suspended", "Suspended"], ["rejected", "Rejected"]];
const sel = "bg-white border border-zinc-200 rounded-sm text-xs px-2 py-1 focus:outline-none focus:border-[#7C3AED] disabled:opacity-50";

const Act = ({ onClick, icon: Icon, label, testid, danger }) => (
  <button onClick={onClick} title={label} data-testid={testid}
    className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-zinc-200 rounded-sm transition-colors ${danger ? "text-zinc-500 hover:border-[#DC2626] hover:text-[#DC2626]" : "text-zinc-600 hover:border-[#7C3AED] hover:text-[#7C3AED]"}`}>
    <Icon size={12} />{label}
  </button>
);

function Detail({ uid, onClose, onSaved }) {
  const { data: u, reload } = usePoll(`/admin/users/${uid}`, 0, [uid]);
  const [notes, setNotes] = useState(null);
  const save = async () => {
    try { await api.post(`/admin/users/${uid}/notes`, { notes: notes ?? u.notes }); toast.success("Notes saved"); reload(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-sm max-w-lg" data-testid="admin-user-detail">
        <DialogHeader><DialogTitle className="font-display">{u?.name || "…"}</DialogTitle></DialogHeader>
        {u && (<div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs"><span className="text-zinc-500">{u.email}</span><RoleChip role={u.role} /><span className={`px-2 py-0.5 rounded-sm font-semibold uppercase text-[10px] ${STATUS_C[u.status]}`}>{u.status}</span></div>
          <div className="grid grid-cols-3 gap-2">
            {[["Paper balance", u.paper.balance != null ? money(u.paper.balance, u.paper.currency || "USD") : "—"], ["Closed trades", u.paper.closed_trades], ["Win rate", u.paper.win_rate != null ? `${u.paper.win_rate}%` : "—"],
              ["Net P/L", money(u.paper.net_pnl)], ["Backtests", u.backtests], ["AI reports", u.ai_reports]].map(([l, v]) => (
              <div key={l} className="border border-zinc-200 p-2.5"><p className="label-xs mb-1">{l}</p><p className="num text-sm">{v}</p></div>
            ))}
          </div>
          <div className="text-xs text-zinc-600 space-y-1">
            <p>Plan: <b>{u.plan_name || "None"}</b>{u.plan_expires_at && ` · expires ${new Date(u.plan_expires_at).toUTCString().slice(5, 16)}`}</p>
            <p>Account manager: <b>{u.account_manager || "Unassigned"}</b></p>
            <p>Registered {u.created_at ? timeAgo(u.created_at) : "—"} · last login {u.last_login ? timeAgo(u.last_login) : "never"}</p>
          </div>
          <label className="block space-y-1"><span className="label-xs">Internal notes</span>
            <textarea rows={3} disabled={!u.manageable} value={notes ?? u.notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-user-notes"
              className="w-full border border-zinc-200 rounded-sm p-2 text-sm focus:outline-none focus:border-[#7C3AED] disabled:bg-zinc-50" /></label>
          {u.manageable && <button onClick={save} className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold rounded-sm" data-testid="admin-user-notes-save">Save notes</button>}
        </div>)}
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { staff, can } = useAdmin();
  const [tab, setTab] = useState("");
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  const params = new URLSearchParams({ ...(tab && { status: tab }), ...(role && { role }), ...(q && { q }) });
  const users = usePoll(`/admin/users?${params}`, 30000, [tab, role, q]);
  const plans = usePoll("/admin/plans", 0);
  const managers = usePoll("/admin/managers", 0);
  const perms = staff.permissions;
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); users.reload(); managers.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const status = (u, s) => call(() => api.post(`/admin/users/${u.id}/status`, { status: s }), `${u.email}: ${s}`);
  return (
    <div data-testid="admin-users-page">
      <PageHeader title={staff.role === "account_manager" ? "My Accounts" : "User Management"} sub={staff.role === "account_manager" ? "Accounts assigned to you" : "Approve, assign, change roles and suspend accounts"} />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {TABS.map(([k, l]) => (
          <button key={l} onClick={() => setTab(k)} data-testid={`users-tab-${l.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${tab === k ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{l}</button>
        ))}
        {perms.view_all_users && (
          <select value={role} onChange={(e) => setRole(e.target.value)} className={`${sel} ml-2 py-1.5`} data-testid="users-role-filter">
            <option value="">All roles</option>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        )}
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="users-search-input"
            className="pl-8 pr-3 py-1.5 text-xs border border-zinc-200 rounded-sm w-60 focus:outline-none focus:border-[#7C3AED]" />
        </div>
      </div>
      <div className="panel overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>{["Account", "Role", "Status", "Plan", "Account manager", "Registered", "Last login", "Actions"].map((h) => <th key={h} className="px-3 py-2 text-left label-xs whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(users.data || []).map((u) => {
              const m = u.manageable;
              return (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50/70 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2.5"><p className="font-medium text-zinc-900">{u.name}{u.id === staff.id && <span className="text-[10px] text-zinc-400 ml-1">(you)</span>}</p><p className="text-xs text-zinc-500">{u.email}</p></td>
                  <td className="px-3 py-2.5">
                    {m && can("change_roles") ? (
                      <select value={u.role} className={sel} data-testid={`user-role-select-${u.id}`}
                        onChange={(e) => call(() => api.post(`/admin/users/${u.id}/role`, { role: e.target.value }), `${u.email} is now ${ROLE_LABEL[e.target.value]}`)}>
                        {perms.assignable_roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : <RoleChip role={u.role} testid={`user-role-${u.id}`} />}
                  </td>
                  <td className="px-3 py-2.5"><span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm ${STATUS_C[u.status]}`} data-testid={`user-status-${u.id}`}>{u.status}</span></td>
                  <td className="px-3 py-2.5">
                    <select value={u.plan_id || ""} disabled={!m} className={sel} data-testid={`user-plan-select-${u.id}`}
                      onChange={(e) => call(() => api.post(`/admin/users/${u.id}/plan`, { plan_id: e.target.value || null }), "Plan updated")}>
                      <option value="">No plan</option>{(plans.data || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    {u.role === "user" && m && can("assign_managers") ? (
                      <select value={u.account_manager_id || ""} className={sel} data-testid={`user-manager-select-${u.id}`}
                        onChange={(e) => call(() => api.post(`/admin/users/${u.id}/manager`, { manager_id: e.target.value || null }), "Account manager updated")}>
                        <option value="">Unassigned</option>{(managers.data || []).map((x) => <option key={x.id} value={x.id}>{x.name || x.email}</option>)}
                      </select>
                    ) : <span className="text-xs text-zinc-500">{u.account_manager || "—"}</span>}
                  </td>
                  <td className="px-3 py-2.5 num text-[11px] text-zinc-500 whitespace-nowrap">{u.created_at ? timeAgo(u.created_at) : "—"}</td>
                  <td className="px-3 py-2.5 num text-[11px] text-zinc-500 whitespace-nowrap">{u.last_login ? timeAgo(u.last_login) : "Never"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      <Act onClick={() => setView(u.id)} icon={Eye} label="View" testid={`view-user-${u.id}`} />
                      {m && u.status === "pending" && <>
                        <Act onClick={() => status(u, "approved")} icon={Check} label="Approve" testid={`approve-user-${u.id}`} />
                        <Act onClick={() => status(u, "rejected")} icon={X} label="Reject" testid={`reject-user-${u.id}`} danger />
                      </>}
                      {m && u.status === "approved" && <Act onClick={() => status(u, "suspended")} icon={Ban} label="Suspend" testid={`suspend-user-${u.id}`} danger />}
                      {m && (u.status === "suspended" || u.status === "rejected") && <Act onClick={() => status(u, "approved")} icon={RotateCcw} label="Reactivate" testid={`reactivate-user-${u.id}`} />}
                      {m && can("delete_users") && <Act onClick={() => window.confirm(`Delete ${u.email} and all their data?`) && call(() => api.delete(`/admin/users/${u.id}`), "User deleted")} icon={Trash2} label="Delete" testid={`delete-user-${u.id}`} danger />}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!users.loading && !users.data?.length && <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500" data-testid="users-empty">No accounts in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {view && <Detail uid={view} onClose={() => setView(null)} onSaved={users.reload} />}
    </div>
  );
}
